import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import lightTheme from './theme';
import { fetchPdfTemplates } from './api/pdfTemplates/pdfTemplateSlice';
import { fetchCountryCodesRequest } from './api/constants/countryCodesSlice';
import { fetchMsicCodesRequest } from './api/constants/msicCodesSlice';
import { fetchStatesRequest } from './api/constants/statesSlice';
import { fetchOrgRequest, fetchOrgSettingsRequest } from './api/organizations/orgSlice';
import { fetchUomCodesRequest } from './api/constants/uomSlice';
import { fetchItemCodesRequest } from './api/constants/itemCodesSlice';
import { fetchTaxTypesRequest } from './api/constants/taxTypesSlice';
import { loadingPrerequisites } from './api/app/appStateSelector';
import { fetchPaymentTermsRequest } from './api/constants/paymentMeansSlice';
import { DashboardLayout, PageContainer } from '@toolpad/core';
import { AppProvider } from '@toolpad/core/AppProvider';
import { Navigate, Route, Routes, Link } from 'react-router-dom';
import UserAccount from './components/UserAccount';
import logo from './assets/logo/marmin_logo.png';
import DescriptionIcon from '@mui/icons-material/Description';
import AuthenticatedRoutes from './routes/AuthenticatedRoutes';
import { BusinessCenterOutlined, DashboardOutlined, Inventory } from '@mui/icons-material';
import PeopleOutlineOutlinedIcon from '@mui/icons-material/PeopleOutlineOutlined';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import SettingsIcon from '@mui/icons-material/Settings';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import BottomBar from './components/BottomBar/BottomBar';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import Loader from './components/Loader';
import GlobalSnackbar from './components/GlobalSnackbar';
import SignupPage from './resources/users/pages/SignupPage';
import ForgotPasswordPage from './resources/forgotPassword/pages/ForgotPasswordPage';
import LoginPage from './resources/login/pages/LoginPage';
import RequestEInvoiceSearchForm from './components/RequestEInvoiceSearchForm';
import RequestEInvoiceCustomerForm from './components/RequestEInvoiceCustomerForm';
import RequestEInvoiceErrorPage from './components/RequestEInvoiceErrorPage';
import ArticleIcon from '@mui/icons-material/Article';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import { fetchCurrenciesRequest } from './api/constants/currencySlice';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { LoginShopify } from './resources/login/pages/LoginShopify';
import SidebarFooter from './components/StickyNavMenus';
import { GlobalStyles } from '@mui/material';
import JsdWidgetProvider from './providers/JsdWidgetProvider';

const NAVIGATION = [
  {
    segment: 'dashboard',
    title: 'MIS Dashboard',
    icon: <DashboardOutlined />,
  },
  {
    kind: 'divider',
  },
  {
    segment: 'business-profiles',
    title: 'Business Profiles',
    pattern: 'business-profiles{/:id}*',
    icon: <BusinessCenterOutlined />,
  },
  {
    segment: 'products',
    title: 'Products/Services',
    pattern: 'products',
    icon: <Inventory />,
  },
  {
    kind: 'divider',
  },
  {
    kind: 'header',
    title: 'Sales',
  },
  {
    segment: 'customers',
    title: 'Customers',
    pattern: 'customers{/:id}*',
    icon: <PeopleOutlineOutlinedIcon />,
  },
  {
    segment: 'documents',
    title: 'Documents',
    icon: <DescriptionIcon />,
  },
  {
    segment: 'drafts',
    title: 'Staged for Consolidation',
    icon: <HourglassTopIcon />,
  },
  {
    kind: 'divider',
  },
  {
    kind: 'header',
    title: 'Purchases',
  },
  {
    segment: 'suppliers',
    title: 'Suppliers',
    icon: <PeopleOutlineOutlinedIcon />,
    pattern: 'suppliers{/:id}*',
  },
  {
    segment: 'self-billed-documents',
    title: 'Self-Billed Documents',
    icon: <DescriptionIcon />,
  },
  {
    kind: 'divider',
  },
  {
    segment: 'bulk-upload',
    title: 'Bulk Upload',
    icon: <CloudUploadOutlinedIcon />,
    pattern: 'bulk-upload{/:id}*',
  },
  {
    segment: 'ai-tools',
    title: 'AI Tools',
    icon: <TipsAndUpdatesIcon />,
    children: [
      {
        segment: 'pdf-data-extraction',
        title: 'PDF Data Extraction',
        icon: <PictureAsPdfIcon />,
      },
    ],
  },
  {
    segment: 'reporting-reconciliation',
    title: 'Reporting & Reconciliation',
    icon: <ArticleIcon />,
    children: [
      {
        segment: 'document-report',
        title: 'Reports',
      },
      {
        segment: 'bulk-pdf-download',
        title: 'Bulk PDF Download',
      },
    ],
  },
  {
    kind: 'divider',
  },
  {
    segment: 'peppol-messages',
    title: 'Incoming Peppol Messages',
    icon: <EmailOutlinedIcon />,
    pattern: 'peppol-messages{/:id}*',
  },
];

function App() {
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const loading = useSelector(loadingPrerequisites);
  const dispatch = useDispatch();
  const totalBusinessProfiles = useSelector((state) => state.org.org.totalBusinessProfiles);
  const isBusinessProfileCreated = useSelector((state) => state.businessProfiles.isBusinessProfileCreated);

  const shouldDisableNav = !totalBusinessProfiles || totalBusinessProfiles === 0;

  useEffect(() => {
    dispatch(fetchCountryCodesRequest({ search_key: 'country' }));
    dispatch(fetchCurrenciesRequest({ search_key: 'currency' }));
    dispatch(fetchMsicCodesRequest({ search_key: 'msic' }));
    dispatch(fetchUomCodesRequest({ search_key: 'uom' }));
    dispatch(fetchStatesRequest({ search_key: 'state' }));
    dispatch(fetchItemCodesRequest({ search_key: 'item_code' }));
    dispatch(fetchTaxTypesRequest({ search_key: 'tax_type' }));
    dispatch(fetchPaymentTermsRequest({ search_key: 'payment_means' }));
  }, [dispatch]);

  useEffect(() => {
    if (isAuthenticated) {
      dispatch(fetchPdfTemplates());
      dispatch(fetchOrgRequest());
      dispatch(fetchOrgSettingsRequest());
    }
  }, [dispatch, isAuthenticated, isBusinessProfileCreated]);

  useEffect(() => {
    if (shouldDisableNav) {
      document.body.classList.add('disable-navigation');
    } else {
      document.body.classList.remove('disable-navigation');
    }

    return () => {
      document.body.classList.remove('disable-navigation');
    };
  }, [shouldDisableNav]);

  if (loading) {
    return <Loader />;
  }

  return (
    <AppProvider theme={lightTheme} navigation={NAVIGATION}>
      {shouldDisableNav && (
        <GlobalStyles
          styles={{
            '.disable-navigation nav, .disable-navigation nav button': {
              opacity: '0.6 !important',
              pointerEvents: 'none !important',
              cursor: 'not-allowed !important',
            },
          }}
        />
      )}

      <Routes>
        <Route path="/request-e-invoice/:businessId" element={<RequestEInvoiceSearchForm />} />
        <Route path="/request-e-invoice/:businessId/customer-info" element={<RequestEInvoiceCustomerForm />} />
        <Route path="/request-e-invoice/:businessId/error" element={<RequestEInvoiceErrorPage />} />
        <Route path="/login/shopify" element={<LoginShopify />} />

        {isAuthenticated ? (
          <Route
            path="/*"
            element={
              <DashboardLayout
                slots={{ toolbarAccount: UserAccount, sidebarFooter: SidebarFooter }}
                branding={{
                  title: '',
                  homeUrl: '/documents',
                  logo: <img src={logo} alt="Marmin logo" />,
                }}
              >
                <PageContainer maxWidth={'xxl'}>
                  <Routes>
                    <Route path="/" element={<Navigate to="/documents" replace />} />
                    {AuthenticatedRoutes(totalBusinessProfiles)}
                  </Routes>
                </PageContainer>
                <BottomBar />
                <JsdWidgetProvider />
              </DashboardLayout>
            }
          />
        ) : (
          <>
            <Route path="/login" element={<LoginPage redirectTo={'/documents'} />} />
            <Route path="/signup" element={<SignupPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="*" element={<Navigate to="/login" />} />
          </>
        )}
      </Routes>

      <Loader />
      <GlobalSnackbar />
    </AppProvider>
  );
}

export default App;
